import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, ArrowRight, Sparkles, Plus, Send, CheckCircle2, Globe, Lock,
  Award, TrendingUp, TrendingDown, ChevronRight, FileText, ExternalLink, X,
  Linkedin, Twitter, Mail as MailIcon, Link2, FileDown, Zap,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const httpAuth = () => ({ Authorization: `Bearer ${localStorage.getItem("wt_token")}` });

const STATUS_TONE = {
  draft:              "bg-slate-100 text-slate-700 ring-slate-200",
  pending_approval:   "bg-amber-100 text-amber-800 ring-amber-200",
  approved:           "bg-sky-100 text-sky-800 ring-sky-200",
  published:          "bg-emerald-100 text-emerald-800 ring-emerald-200",
};
const STATUS_LABEL = {
  draft: "Draft", pending_approval: "Pending approval",
  approved: "Approved", published: "Published",
};

export default function CaseStudyEngine() {
  const { slug } = useParams();
  if (slug) return <PublicCaseStudy slug={slug} />;
  return <ProofDashboard />;
}

/* ==========================================================================
 * A · PUBLIC PROOF PAGE — /proof/:slug
 * ========================================================================== */
function PublicCaseStudy({ slug }) {
  const [doc, setDoc] = useState(null);
  const [share, setShare] = useState(null);
  const [state, setState] = useState("loading");
  useEffect(() => {
    document.title = "Case Study | WebsiteTech.pro";
    Promise.all([
      axios.get(`${API}/case-studies/public/${slug}`),
      axios.get(`${API}/case-studies/public/${slug}/share-meta?base_url=${encodeURIComponent(window.location.origin)}`),
    ]).then(([r1, r2]) => {
      setDoc(r1.data);
      setShare(r2.data);
      // Inject OG tags dynamically for LinkedIn/Twitter preview
      try {
        const meta = (name, content, prop) => {
          const attr = prop ? "property" : "name";
          const key = prop || name;
          let tag = document.querySelector(`meta[${attr}="${key}"]`);
          if (!tag) { tag = document.createElement("meta"); tag.setAttribute(attr, key); document.head.appendChild(tag); }
          tag.setAttribute("content", content);
        };
        meta("og:title", r2.data.og_title, "og:title");
        meta("og:description", r2.data.og_description, "og:description");
        meta("og:image", r2.data.og_image, "og:image");
        meta("og:url", r2.data.canonical, "og:url");
        meta("og:type", "article", "og:type");
        meta("twitter:card", "summary_large_image");
        meta("twitter:image", r2.data.og_image);
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
        canonical.href = r2.data.canonical;
      } catch {}
      setState("ok");
    }).catch(() => setState("missing"));
  }, [slug]);
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(share.canonical); toast.success("Link copied"); }
    catch { toast.error("Copy failed"); }
  };
  if (state === "loading") return (
    <div className="min-h-screen grid place-items-center bg-slate-50">
      <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
    </div>
  );
  if (state === "missing") return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold text-slate-900">Case study not found</h1>
        <p className="text-slate-500 mt-2">It may be private or unpublished.</p>
        <Link to="/proof" className="mt-4 inline-flex items-center gap-1 text-orange-600 font-bold">
          Back to Proof Wall <ArrowRight className="w-4 h-4" />
        </Link>
      </main>
    </div>
  );

  const b = doc.baseline;
  const o = doc.outcome;
  const delta = doc.delta;
  return (
    <div className="min-h-screen bg-slate-50" data-testid="case-study-public">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="rounded-2xl bg-gradient-to-br from-orange-50 via-white to-emerald-50 ring-1 ring-slate-200 p-6 sm:p-10">
          <div className="text-[11px] uppercase tracking-widest text-orange-600 font-bold">
            WebsiteTech · Growth Case Study
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold text-slate-900 mt-2"
              data-testid="case-study-title">{doc.title}</h1>
          {doc.client_name && (
            <div className="text-slate-600 mt-1">Client · {doc.client_name}</div>
          )}
          {doc.summary && (
            <p className="text-slate-700 mt-3 max-w-3xl text-lg">{doc.summary}</p>
          )}
          {delta && (
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <BigDelta v={delta.overall_delta} label="Overall growth score" />
              {["build", "be_found", "build_trust", "convert_grow"].map(k => (
                <SmallDelta key={k} k={k} v={delta.score_delta?.[k] ?? 0} />
              ))}
            </div>
          )}
        </div>

        {/* Social share bar */}
        {share && (
          <section className="mt-6 rounded-xl bg-white ring-1 ring-slate-200 p-4 flex flex-wrap items-center gap-3"
                   data-testid="case-study-share-bar">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Share this story</div>
              <div className="text-sm text-slate-700 mt-0.5">Every share = one more buyer who sees real proof.</div>
            </div>
            <a href={share.linkedin_share} target="_blank" rel="noreferrer"
                data-testid="case-study-share-linkedin"
                className="inline-flex items-center gap-1 rounded-lg bg-[#0077b5] hover:bg-[#005f8f] text-white px-3 py-2 text-sm font-bold">
              <Linkedin className="w-4 h-4" /> LinkedIn
            </a>
            <a href={share.twitter_share} target="_blank" rel="noreferrer"
                data-testid="case-study-share-twitter"
                className="inline-flex items-center gap-1 rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-3 py-2 text-sm font-bold">
              <Twitter className="w-4 h-4" /> X / Twitter
            </a>
            <a href={share.email_share}
                data-testid="case-study-share-email"
                className="inline-flex items-center gap-1 rounded-lg ring-1 ring-slate-200 bg-white hover:bg-slate-50 text-slate-800 px-3 py-2 text-sm font-bold">
              <MailIcon className="w-4 h-4" /> Email
            </a>
            {share.pdf_url && (
              <a href={share.pdf_url} target="_blank" rel="noreferrer"
                  data-testid="case-study-share-pdf"
                  className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 text-sm font-bold">
                <FileDown className="w-4 h-4" /> Download PDF
              </a>
            )}
            <button onClick={copyLink}
                    data-testid="case-study-share-copy"
                    className="inline-flex items-center gap-1 rounded-lg ring-1 ring-slate-200 bg-white hover:bg-slate-50 text-slate-800 px-3 py-2 text-sm font-bold">
              <Link2 className="w-4 h-4" /> Copy link
            </button>
          </section>
        )}
        <div className="hidden">{/* separator */}</div>

        {/* Before/After */}
        {b && o && (
          <section className="mt-6 grid md:grid-cols-2 gap-4" data-testid="case-study-before-after">
            <SnapshotCard title="Baseline" chip="Before" snap={b} tone="rose" />
            <SnapshotCard title="Outcome" chip="After" snap={o} tone="emerald" />
          </section>
        )}

        {/* Interventions timeline */}
        {(doc.interventions || []).length > 0 && (
          <section className="mt-6 rounded-xl bg-white ring-1 ring-slate-200 p-5"
                   data-testid="case-study-interventions">
            <h2 className="font-bold text-slate-900 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-orange-500" /> What we changed
            </h2>
            <ol className="mt-3 border-l-2 border-slate-200 pl-4 space-y-4">
              {doc.interventions.map(it => (
                <li key={it.id} className="relative">
                  <span className="absolute -left-[10px] top-1.5 w-3 h-3 bg-orange-500 rounded-full ring-2 ring-white" />
                  <div className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">
                    {new Date(it.date).toLocaleDateString()}
                  </div>
                  <div className="font-bold text-slate-900 mt-0.5">{it.title}</div>
                  {it.detail && <div className="text-sm text-slate-700 mt-1">{it.detail}</div>}
                  {(it.metric_before || it.metric_after) && (
                    <div className="mt-2 inline-flex items-center gap-2 text-xs">
                      <span className="rounded-full bg-slate-100 px-2 py-0.5">
                        Before · <b>{it.metric_before || "—"}</b>
                      </span>
                      <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                      <span className="rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5">
                        After · <b>{it.metric_after || "—"}</b>
                      </span>
                    </div>
                  )}
                </li>
              ))}
            </ol>
          </section>
        )}

        <div className="mt-8 rounded-xl bg-slate-900 text-white p-5 flex flex-wrap items-center gap-4 justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-orange-300 font-bold">Your turn</div>
            <div className="text-xl font-bold mt-1">Run your free growth audit</div>
            <div className="text-slate-300 text-sm mt-1">Get the same before-and-after roadmap for your site — free.</div>
          </div>
          <Link to="/growth-audit"
                className="rounded-lg bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 text-sm font-bold inline-flex items-center gap-1"
                data-testid="case-study-cta">
            Start my audit <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </main>
    </div>
  );
}

/* ==========================================================================
 * B · DASHBOARD — /proof
 * ========================================================================== */
function ProofDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [mine, setMine]       = useState([]);
  const [publicFeed, setPublicFeed] = useState([]);
  const [audits, setAudits]   = useState([]);
  const [role, setRole]       = useState("client");
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId]   = useState(null);

  const [form, setForm] = useState({ title: "", client_name: "", industry: "other",
                                       summary: "", audit_id: "" });
  const [prefs, setPrefs] = useState({ auto_publish_enabled: false, auto_publish_visibility: "public", auto_publish_min_delta: 0 });
  const [prefBusy, setPrefBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [{ data: p }, { data: aud }] = await Promise.all([
        axios.get(`${API}/case-studies/public`),
        localStorage.getItem("wt_token")
          ? axios.get(`${API}/growth-audit/my-audits`, { headers: httpAuth() })
          : Promise.resolve({ data: { audits: [] } }),
      ]);
      setPublicFeed(p.case_studies || []);
      setAudits(aud.audits || []);
      if (localStorage.getItem("wt_token")) {
        const { data: m } = await axios.get(`${API}/case-studies/mine`, { headers: httpAuth() });
        setMine(m.case_studies || []);
        setRole(m.role || "client");
        try {
          const { data: pr } = await axios.get(`${API}/case-studies/prefs`,
                                                  { headers: httpAuth() });
          setPrefs(pr);
        } catch {}
      }
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    document.title = "Proof Wall · Case Studies | WebsiteTech.pro";
    if (user === undefined) return;
    (async () => { await reload(); setLoading(false); })();
  }, [user, reload]);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/case-studies`, form,
                                          { headers: httpAuth() });
      toast.success("Draft created");
      setForm({ title: "", client_name: "", industry: "other", summary: "", audit_id: "" });
      setOpenId(data.case_study.id);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setCreating(false); }
  };

  if (loading) return (
    <div className="min-h-screen grid place-items-center bg-slate-50">
      <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="proof-page">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 py-8 grid gap-6">
        <header>
          <div className="text-[11px] uppercase tracking-widest text-orange-600 font-bold">
            WebsiteTech · Growth System
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-1"
              data-testid="proof-title">Proof Wall</h1>
          <p className="text-slate-600 mt-1 max-w-3xl">
            Every completed audit becomes a public before-and-after story — with the client's approval — so future buyers can see real outcomes.
          </p>
        </header>

        {/* Public feed */}
        <section className="rounded-xl bg-white ring-1 ring-slate-200 p-5" data-testid="proof-public-feed">
          <h2 className="font-bold text-slate-900">Published stories ({publicFeed.length})</h2>
          {publicFeed.length === 0 && (
            <div className="text-sm text-slate-500 py-4">
              No published case studies yet. Yours could be first.
            </div>
          )}
          <div className="mt-3 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {publicFeed.map(c => (
              <Link key={c.id} to={`/proof/${c.slug}`}
                    data-testid={`proof-public-${c.slug}`}
                    className="rounded-xl ring-1 ring-slate-200 hover:ring-slate-400 hover:shadow-md transition p-4 bg-gradient-to-br from-orange-50 to-white">
                <div className="text-[10px] uppercase tracking-widest font-bold text-orange-600">
                  {c.industry.replace("-", " ")}
                </div>
                <div className="font-bold text-slate-900 text-lg mt-1">{c.title}</div>
                {c.delta && (
                  <div className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-emerald-700">
                    <TrendingUp className="w-3.5 h-3.5" /> +{c.delta.overall_delta} overall score
                  </div>
                )}
                <div className="mt-2 text-sm text-slate-600 line-clamp-2">{c.summary}</div>
              </Link>
            ))}
          </div>
        </section>

        {/* Auth section */}
        {!user && (
          <div className="rounded-xl bg-white ring-1 ring-slate-200 p-6 text-center"
               data-testid="proof-signin-cta">
            <Award className="w-8 h-8 text-slate-400 mx-auto" />
            <div className="font-bold text-slate-900 mt-2">Working on a case study?</div>
            <p className="text-slate-600 text-sm mt-1 max-w-lg mx-auto">
              Sign in to draft a proof story from any of your audits.
            </p>
            <Link to="/login?redirect=/proof"
                  className="mt-4 inline-flex items-center gap-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-sm font-bold">
              Sign in <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {user && (
          <>
            {/* Auto-publish (admin only) */}
            {role === "admin" && (
              <section className="rounded-xl bg-gradient-to-br from-orange-50 via-white to-emerald-50 ring-1 ring-orange-200 p-4 flex flex-wrap items-center gap-3"
                       data-testid="proof-auto-publish">
                <Zap className="w-5 h-5 text-orange-500 shrink-0" />
                <div className="flex-1 min-w-[220px]">
                  <div className="text-[10px] uppercase tracking-widest text-orange-600 font-bold">
                    Auto-publish
                  </div>
                  <div className="text-sm text-slate-700">
                    When on, any new case study auto-publishes the moment both baseline and outcome are captured — <b>only if</b> the score improvement clears the threshold below. Weak stories stay in draft.
                  </div>
                </div>
                <label className="flex flex-col text-xs">
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-0.5">
                    Min Δ score
                  </span>
                  <input type="number" min="0" max="100"
                          value={prefs.auto_publish_min_delta}
                          onChange={async (e) => {
                            const val = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                            setPrefs(p => ({ ...p, auto_publish_min_delta: val }));
                          }}
                          onBlur={async (e) => {
                            const val = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                            setPrefBusy(true);
                            try {
                              const { data } = await axios.post(`${API}/case-studies/prefs`,
                                  { enabled: prefs.auto_publish_enabled,
                                    visibility: prefs.auto_publish_visibility,
                                    min_score_delta: val },
                                  { headers: httpAuth() });
                              setPrefs(data);
                            } catch { toast.error("Save failed"); }
                            finally { setPrefBusy(false); }
                          }}
                          disabled={prefBusy}
                          data-testid="proof-auto-publish-min-delta"
                          className="w-20 rounded-lg ring-1 ring-slate-200 px-2 py-1.5 text-sm font-bold" />
                </label>
                <select value={prefs.auto_publish_visibility}
                         onChange={async (e) => {
                           setPrefBusy(true);
                           try {
                             const { data } = await axios.post(`${API}/case-studies/prefs`,
                                 { enabled: prefs.auto_publish_enabled,
                                   visibility: e.target.value,
                                   min_score_delta: prefs.auto_publish_min_delta },
                                 { headers: httpAuth() });
                             setPrefs(data);
                           } catch (err) { toast.error("Save failed"); }
                           finally { setPrefBusy(false); }
                         }}
                         disabled={prefBusy}
                         data-testid="proof-auto-publish-visibility"
                         className="rounded-lg ring-1 ring-slate-200 px-2 py-1.5 text-xs font-bold text-slate-700">
                  <option value="public">Public</option>
                  <option value="private">Private link</option>
                </select>
                <button onClick={async () => {
                          setPrefBusy(true);
                          try {
                            const { data } = await axios.post(`${API}/case-studies/prefs`,
                                { enabled: !prefs.auto_publish_enabled,
                                  visibility: prefs.auto_publish_visibility,
                                  min_score_delta: prefs.auto_publish_min_delta },
                                { headers: httpAuth() });
                            setPrefs(data);
                            toast.success(data.auto_publish_enabled
                              ? `Auto-publish ON · min Δ ${data.auto_publish_min_delta}`
                              : "Auto-publish OFF");
                          } catch { toast.error("Toggle failed"); }
                          finally { setPrefBusy(false); }
                        }}
                        disabled={prefBusy}
                        data-testid="proof-auto-publish-toggle"
                        className={`rounded-lg px-4 py-2 text-sm font-bold ${
                          prefs.auto_publish_enabled
                            ? "bg-emerald-500 hover:bg-emerald-600 text-white"
                            : "bg-slate-900 hover:bg-slate-800 text-white"}`}>
                  {prefs.auto_publish_enabled ? "On — turn off" : "Turn on"}
                </button>
              </section>
            )}

            {/* Create draft */}
            <section className="rounded-xl bg-white ring-1 ring-slate-200 p-5" data-testid="proof-create">
              <h2 className="font-bold text-slate-900">Start a new case study</h2>
              <div className="grid md:grid-cols-2 gap-3 mt-3">
                <F label="Title" full>
                  <input value={form.title}
                          onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                          placeholder="e.g. Sunrise Dental — from 34 to 82 in 90 days"
                          data-testid="proof-title-in"
                          className="w-full rounded-lg ring-1 ring-slate-200 px-3 py-2 text-sm" />
                </F>
                <F label="Client name">
                  <input value={form.client_name}
                          onChange={(e) => setForm(f => ({ ...f, client_name: e.target.value }))}
                          data-testid="proof-client-in"
                          className="w-full rounded-lg ring-1 ring-slate-200 px-3 py-2 text-sm" />
                </F>
                <F label="Baseline audit (optional)">
                  <select value={form.audit_id}
                           onChange={(e) => setForm(f => ({ ...f, audit_id: e.target.value }))}
                           data-testid="proof-audit-in"
                           className="w-full rounded-lg ring-1 ring-slate-200 px-3 py-2 text-sm">
                    <option value="">— no audit yet —</option>
                    {audits.map(a => (
                      <option key={a.id} value={a.id}>{a.domain} · {new Date(a.created_at).toLocaleDateString()}</option>
                    ))}
                  </select>
                </F>
                <F label="1-line summary" full>
                  <input value={form.summary}
                          onChange={(e) => setForm(f => ({ ...f, summary: e.target.value }))}
                          placeholder="What did we set out to fix?"
                          data-testid="proof-summary-in"
                          className="w-full rounded-lg ring-1 ring-slate-200 px-3 py-2 text-sm" />
                </F>
              </div>
              <button onClick={create} disabled={creating}
                       data-testid="proof-create-btn"
                       className="mt-4 rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-sm font-bold inline-flex items-center gap-1 disabled:opacity-50">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Create draft
              </button>
            </section>

            {/* My case studies */}
            <section className="rounded-xl bg-white ring-1 ring-slate-200 p-5" data-testid="proof-mine">
              <h2 className="font-bold text-slate-900">
                {role === "admin" ? "All case studies" : "My case studies"} ({mine.length})
              </h2>
              {mine.length === 0 && (
                <div className="text-sm text-slate-500 py-4">
                  You haven't drafted any yet — start above.
                </div>
              )}
              <ul className="mt-3 divide-y divide-slate-100">
                {mine.map(c => (
                  <li key={c.id}
                      className="py-3 flex items-center gap-3 hover:bg-slate-50 rounded-lg px-2 cursor-pointer"
                      onClick={() => setOpenId(c.id)}
                      data-testid={`proof-mine-${c.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-slate-900 truncate">{c.title}</div>
                      <div className="text-xs text-slate-500">
                        {c.client_name || "unnamed client"} · updated {new Date(c.updated_at).toLocaleDateString()}
                      </div>
                    </div>
                    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ring-1 ring-inset ${STATUS_TONE[c.status] || STATUS_TONE.draft}`}>
                      {STATUS_LABEL[c.status]}
                    </span>
                    {c.status === "published" && (
                      c.visibility === "public"
                        ? <Globe className="w-4 h-4 text-emerald-600" />
                        : <Lock className="w-4 h-4 text-slate-400" />
                    )}
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </li>
                ))}
              </ul>
            </section>
          </>
        )}
      </main>

      {openId && <CaseStudyDrawer sid={openId} role={role} audits={audits}
                                     onClose={() => { setOpenId(null); reload(); }} />}
    </div>
  );
}

/* -------------------------- Drawer for editor ----------------------------- */
function CaseStudyDrawer({ sid, role, audits, onClose }) {
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [interv, setInterv] = useState({ title: "", detail: "", metric_before: "", metric_after: "" });
  const [outcomeAudit, setOutcomeAudit] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/case-studies/${sid}`, { headers: httpAuth() });
      setDoc(data);
    } catch (e) { toast.error("Could not load case study"); }
  }, [sid]);

  useEffect(() => { load(); }, [load]);

  const setOutcome = async () => {
    if (!outcomeAudit) return toast.error("Pick an outcome audit first");
    setBusy(true);
    try {
      await axios.post(`${API}/case-studies/${sid}/outcome`,
                        { audit_id: outcomeAudit }, { headers: httpAuth() });
      toast.success("Outcome captured");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const addIntervention = async () => {
    if (!interv.title.trim()) return toast.error("Intervention title needed");
    setBusy(true);
    try {
      await axios.post(`${API}/case-studies/${sid}/intervention`, interv,
                        { headers: httpAuth() });
      toast.success("Intervention added");
      setInterv({ title: "", detail: "", metric_before: "", metric_after: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/case-studies/${sid}/submit`, {}, { headers: httpAuth() });
      toast.success("Sent for approval");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/case-studies/${sid}/approve`, {}, { headers: httpAuth() });
      toast.success("Approved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const publish = async (visibility) => {
    setBusy(true);
    try {
      await axios.post(`${API}/case-studies/${sid}/publish`, { visibility },
                        { headers: httpAuth() });
      toast.success(`Published (${visibility})`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!doc) return null;
  const publicUrl = doc.status === "published" && doc.visibility === "public"
    ? `${window.location.origin}/proof/${doc.slug}` : null;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="proof-drawer">
      <div className="flex-1 bg-slate-900/40" onClick={onClose} />
      <aside className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-xl">
        <div className="p-5 border-b border-slate-200 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-orange-600">
              {doc.industry}
            </div>
            <h2 className="text-xl font-bold text-slate-900 mt-0.5">{doc.title}</h2>
            <div className="mt-1 flex items-center gap-2">
              <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ring-1 ring-inset ${STATUS_TONE[doc.status]}`}>
                {STATUS_LABEL[doc.status]}
              </span>
              {publicUrl && (
                <a href={publicUrl} target="_blank" rel="noreferrer"
                    className="text-xs text-orange-600 font-bold inline-flex items-center gap-1"
                    data-testid="proof-drawer-url">
                  view public <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"
                  data-testid="proof-drawer-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Baseline */}
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Baseline</div>
            {doc.baseline ? (
              <SnapshotCard title="Baseline" chip="Before" snap={doc.baseline} tone="rose" />
            ) : (
              <div className="text-sm text-slate-500">
                No baseline yet. Create the case study from a growth audit next time.
              </div>
            )}
          </div>

          {/* Outcome */}
          <div data-testid="proof-drawer-outcome">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Outcome</div>
            {doc.outcome ? (
              <SnapshotCard title="Outcome" chip="After" snap={doc.outcome} tone="emerald" />
            ) : (
              <div className="flex flex-col sm:flex-row gap-2">
                <select value={outcomeAudit} onChange={(e) => setOutcomeAudit(e.target.value)}
                         data-testid="proof-drawer-outcome-audit"
                         className="flex-1 rounded-lg ring-1 ring-slate-200 px-3 py-2 text-sm">
                  <option value="">Pick the re-audit that shows results</option>
                  {audits.map(a => (
                    <option key={a.id} value={a.id}>{a.domain} · {new Date(a.created_at).toLocaleDateString()}</option>
                  ))}
                </select>
                <button onClick={setOutcome} disabled={busy || !outcomeAudit}
                         data-testid="proof-drawer-set-outcome"
                         className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50">
                  Capture outcome
                </button>
              </div>
            )}
          </div>

          {doc.delta && (
            <div className="rounded-lg bg-emerald-50 ring-1 ring-emerald-200 p-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-700" />
              <div className="text-sm text-emerald-900">
                Overall score change: <b>{doc.delta.overall_delta > 0 ? "+" : ""}{doc.delta.overall_delta}</b>
              </div>
            </div>
          )}

          {/* Interventions */}
          <div data-testid="proof-drawer-interventions">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">
              Interventions ({(doc.interventions || []).length})
            </div>
            <ol className="space-y-2 mb-3">
              {(doc.interventions || []).map(it => (
                <li key={it.id} className="rounded-lg ring-1 ring-slate-200 bg-slate-50 p-3 text-sm">
                  <div className="font-bold text-slate-900">{it.title}</div>
                  {it.detail && <div className="text-slate-600 mt-1">{it.detail}</div>}
                  <div className="text-[11px] text-slate-500 mt-1">
                    {new Date(it.date).toLocaleDateString()}
                    {(it.metric_before || it.metric_after) && ` · ${it.metric_before || "?"} → ${it.metric_after || "?"}`}
                  </div>
                </li>
              ))}
            </ol>
            <div className="grid grid-cols-2 gap-2">
              <input value={interv.title}
                      onChange={(e) => setInterv(i => ({ ...i, title: e.target.value }))}
                      placeholder="What did you change?"
                      className="col-span-2 rounded-lg ring-1 ring-slate-200 px-3 py-2 text-sm"
                      data-testid="proof-drawer-int-title" />
              <input value={interv.metric_before}
                      onChange={(e) => setInterv(i => ({ ...i, metric_before: e.target.value }))}
                      placeholder="Metric before"
                      className="rounded-lg ring-1 ring-slate-200 px-3 py-2 text-sm"
                      data-testid="proof-drawer-int-before" />
              <input value={interv.metric_after}
                      onChange={(e) => setInterv(i => ({ ...i, metric_after: e.target.value }))}
                      placeholder="Metric after"
                      className="rounded-lg ring-1 ring-slate-200 px-3 py-2 text-sm"
                      data-testid="proof-drawer-int-after" />
              <textarea value={interv.detail} rows={2}
                         onChange={(e) => setInterv(i => ({ ...i, detail: e.target.value }))}
                         placeholder="Detail (optional)"
                         className="col-span-2 rounded-lg ring-1 ring-slate-200 px-3 py-2 text-sm"
                         data-testid="proof-drawer-int-detail" />
              <button onClick={addIntervention} disabled={busy}
                       className="col-span-2 rounded-lg ring-1 ring-slate-200 bg-white hover:bg-slate-50 px-3 py-2 text-sm font-bold inline-flex items-center gap-1 justify-center"
                       data-testid="proof-drawer-int-add">
                <Plus className="w-4 h-4" /> Add intervention
              </button>
            </div>
          </div>

          {/* Workflow buttons */}
          <div className="border-t border-slate-200 pt-4 space-y-2">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Workflow</div>
            {doc.status === "draft" && (
              <button onClick={submit} disabled={busy || !doc.baseline || !doc.outcome}
                       data-testid="proof-drawer-submit"
                       className="w-full rounded-lg bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 text-sm font-bold inline-flex items-center gap-1 justify-center disabled:opacity-50">
                <Send className="w-4 h-4" /> Submit for approval
              </button>
            )}
            {doc.status === "pending_approval" && role === "admin" && (
              <button onClick={approve} disabled={busy}
                       data-testid="proof-drawer-approve"
                       className="w-full rounded-lg bg-sky-500 hover:bg-sky-600 text-white px-4 py-2 text-sm font-bold inline-flex items-center gap-1 justify-center">
                <CheckCircle2 className="w-4 h-4" /> Approve
              </button>
            )}
            {doc.status === "pending_approval" && role !== "admin" && (
              <div className="text-xs text-slate-500 text-center">
                Waiting on admin approval. You'll be notified when it's approved.
              </div>
            )}
            {doc.status === "approved" && role === "admin" && (
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => publish("public")} disabled={busy}
                         data-testid="proof-drawer-publish-public"
                         className="rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 text-sm font-bold inline-flex items-center gap-1 justify-center">
                  <Globe className="w-4 h-4" /> Publish · public
                </button>
                <button onClick={() => publish("private")} disabled={busy}
                         data-testid="proof-drawer-publish-private"
                         className="rounded-lg ring-1 ring-slate-200 bg-white hover:bg-slate-50 px-4 py-2 text-sm font-bold text-slate-700 inline-flex items-center gap-1 justify-center">
                  <Lock className="w-4 h-4" /> Publish · private link
                </button>
              </div>
            )}
            {doc.status === "published" && (
              <div className="text-xs text-slate-500 text-center flex items-center gap-1 justify-center">
                {doc.visibility === "public"
                  ? <><Globe className="w-3.5 h-3.5 text-emerald-600" /> Public — visible on the Proof Wall</>
                  : <><Lock className="w-3.5 h-3.5" /> Private — only reachable via direct link</>}
              </div>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}

/* ============================ Helpers UI ================================= */
function F({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

function SnapshotCard({ title, chip, snap, tone }) {
  const toneMap = {
    rose:     "from-rose-50 to-white ring-rose-200",
    emerald:  "from-emerald-50 to-white ring-emerald-200",
  };
  return (
    <div className={`rounded-xl bg-gradient-to-br ${toneMap[tone]} ring-1 p-4`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{title}</div>
        <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-full ${
          tone === "rose" ? "bg-rose-100 text-rose-800" : "bg-emerald-100 text-emerald-800"}`}>
          {chip}
        </span>
      </div>
      <div className="mt-1 text-4xl font-extrabold text-slate-900">
        {snap.overall_score}<span className="text-sm text-slate-400">/100</span>
      </div>
      <div className="text-xs text-slate-500 mt-1">
        Captured {new Date(snap.captured_at).toLocaleDateString()}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-1 text-xs">
        {Object.entries(snap.scores || {}).map(([k, v]) => (
          <div key={k} className="flex justify-between">
            <span className="text-slate-500">{k.replace("_", " ")}</span>
            <b className="text-slate-800">{v}</b>
          </div>
        ))}
      </div>
      {snap.top_issues?.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Top issues</div>
          <ul className="text-xs text-slate-700 list-disc pl-4 mt-1 space-y-0.5">
            {snap.top_issues.slice(0, 3).map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function BigDelta({ v, label }) {
  const up = v >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  const tone = up ? "text-emerald-700" : "text-rose-700";
  return (
    <div className="rounded-xl bg-white ring-1 ring-slate-200 px-4 py-3 flex items-center gap-3">
      <Icon className={`w-6 h-6 ${tone}`} />
      <div>
        <div className={`text-3xl font-extrabold ${tone}`}>{up ? "+" : ""}{v}</div>
        <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
      </div>
    </div>
  );
}

function SmallDelta({ k, v }) {
  const up = v >= 0;
  return (
    <span className={`text-xs rounded-full px-2 py-1 ring-1 ring-inset ${
      up ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
         : "bg-rose-50 text-rose-700 ring-rose-200"}`}>
      {k.replace("_", " ")} · <b>{up ? "+" : ""}{v}</b>
    </span>
  );
}
